import { useFormContext } from 'react-hook-form'
import { GetEventTypeAvailabilities200 } from '@cocoonspace/sdk-js/v2/types/getEventTypeAvailabilities200'
import { Button } from '@cocoonspace/ui/components/button'
import { FormField } from '@cocoonspace/ui/components/form'
import { Trans, useLingui } from '@lingui/react/macro'

import { useAnswerCard } from './provider'
import type { BookingRequestAnswerFormData } from '.'
import type { ProcessedSlot } from './use-available-time-slots'

const HourItem = ({
	value,
	timeSlot,
	onChange,
}: {
	value?: string
	timeSlot: { time: string; spaceId: string }
	onChange: (value: { time: string; spaceId: string }) => void
}) => {
	const isSelected = value === timeSlot.time

	return (
		<Button
			variant={isSelected ? 'default' : 'outline'}
			size='sm'
			className='border-border w-full !py-5 text-left transition-colors'
			onClick={() => onChange(timeSlot)}
		>
			{timeSlot.time}
		</Button>
	)
}

const EmptyContent = () => {
	return (
		<div className='grid h-full items-center p-4 text-center text-sm'>
			<Trans>Aucun créneau disponible</Trans>
		</div>
	)
}

const TimePickerList = ({
	slots,
	day,
}: {
	slots: ProcessedSlot[]
	day: string
}) => {
	const { t } = useLingui()
	const { setStep } = useAnswerCard()
	const form = useFormContext<BookingRequestAnswerFormData>()

	if (!slots.length) return <EmptyContent />

	return (
		<>
			<FormField
				name='start'
				render={({ field }) => (
					<div className='flex h-[320px] flex-col overflow-hidden'>
						<div className='grid flex-1 grid-cols-2 place-content-start gap-2 overflow-y-auto p-4'>
							{slots.map((slot) => (
								<HourItem
									key={`${slot.time}-${slot.spaceId}`}
									timeSlot={slot}
									value={field.value}
									onChange={(v) => {
										form.setValue('requestedLocation', {
											type: 'cocoon',
											space_id: v.spaceId,
										})

										return field.onChange(v.time)
									}}
								/>
							))}
						</div>
					</div>
				)}
			/>

			<div className='flex justify-end p-4'>
				<Button
					// size='sm'
					className='w-full'
					disabled={
						!day ||
						!form.watch('start') ||
						!form.watch('requestedLocation')?.space_id
					}
					onClick={() => setStep('validation')}
				>
					{t`Valider`}
				</Button>
			</div>
		</>
	)
}

const TimePickerContent = ({
	day,
	slots,
}: {
	day: string
	slots?: ProcessedSlot[]
}) => {
	if (!slots?.length) return <EmptyContent />

	return (
		<TimePickerList
			slots={slots}
			day={day}
		/>
	)
}

const SelectDay = () => {
	const { t } = useLingui()

	return (
		<div className='flex h-full flex-1 items-center justify-center text-center text-sm'>
			{t`Sélectionnez une date`}
		</div>
	)
}

export const TimePicker = ({
	monthSlots,
}: {
	monthSlots?: GetEventTypeAvailabilities200
}) => {
	const form = useFormContext<BookingRequestAnswerFormData>()
	const day = form.watch('day')

	if (!day) {
		return (
			<div className='flex min-h-[100px] w-[350px] flex-col divide-y max-md:max-h-[250px]'>
				<SelectDay />
			</div>
		)
	}
	const slots = monthSlots?.[day]

	return (
		<div className='flex min-h-[100px] w-[350px] flex-col divide-y max-md:max-h-[250px]'>
			<TimePickerContent
				day={day}
				slots={slots ?? []}
			/>
		</div>
	)
}
